import type { IOffersRequest } from "@commonTypes/offer"
import { skipToken, useMutation, useQuery } from "@tanstack/react-query"
import {
  getAllPlans,
  getContractTypes,
  getOffer,
  findOrCreateOffer,
  getMarketsByZipCode,
  getDynamicOffers,
  getRecentlyViewedOffers,
  getPlanRewards,
  getPlansSOP,
  getMarkets,
} from "@apis/offers/offers"
import { offersKeys } from "@apis/offers/queryKeys"
import type { AxiosResponse } from "axios"
import { serializeDataToValueLabel } from "@utils/formatters"

type WithRequestId<T> = T & { requestId: string }

const selectWithRequestId = <T>(
  response: AxiosResponse<T>,
): WithRequestId<T> => {
  const requestId = response.headers["x-request-id"]?.split("-")[0] ?? ""
  const data: T = response.data
  if (Array.isArray(data)) {
    const arr = [...data] as WithRequestId<T>
    arr.requestId = requestId
    return arr
  }
  return { ...data, requestId } as WithRequestId<T>
}

export const useGetOfferQuery = (offer_code: string | undefined) => {
  return useQuery({
    queryKey: offersKeys.detail(offer_code),
    queryFn: offer_code ? () => getOffer(offer_code) : skipToken,
  })
}

export const useGetMarketsByZipCodeQuery = (zip_code: string | undefined) => {
  return useQuery({
    queryKey: offersKeys.marketsByZipCode(zip_code),
    queryFn: zip_code ? () => getMarketsByZipCode(zip_code) : skipToken,
  })
}

export const useGetDynamicOffersQuery = (
  params: IOffersRequest | undefined,
) => {
  return useQuery({
    queryKey: offersKeys.dynamicOffers(params),
    queryFn: params ? () => getDynamicOffers(params) : skipToken,
    select: selectWithRequestId,
  })
}

export const useGetRecentlyViewedOffersQuery = (
  market_code: string | undefined,
  prospect_guid: string | undefined,
) => {
  return useQuery({
    queryKey: offersKeys.recentlyViewedOffers(market_code, prospect_guid),
    queryFn:
      market_code && prospect_guid
        ? () => getRecentlyViewedOffers(market_code, prospect_guid)
        : skipToken,
    select: selectWithRequestId,
  })
}

export const useGetContractTypesQuery = () => {
  return useQuery({
    queryKey: offersKeys.contractTypes(),
    queryFn: () => getContractTypes(),
  })
}

export const useGetAllPlansQuery = () => {
  return useQuery({
    queryKey: offersKeys.allPlans(),
    queryFn: () => getAllPlans(),
  })
}

export const useGetPlanSOPQuery = () => {
  return useQuery({
    queryKey: offersKeys.planSOP(),
    queryFn: () => getPlansSOP(),
  })
}

export const useGetPlanRewardsQuery = (plan_code: string) => {
  return useQuery({
    queryKey: offersKeys.planRewards(plan_code),
    queryFn: () => getPlanRewards(plan_code),
  })
}

export const useFindOrCreateOfferMutation = () => {
  return useMutation({
    mutationFn: findOrCreateOffer,
  })
}

export const useGetMarketsQuery = () => {
  return useQuery({
    queryKey: offersKeys.markets(),
    queryFn: () => getMarkets(),
    select: (data) => serializeDataToValueLabel(data, "market_code"),
  })
}
